import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import {
  Building2, Plus, RefreshCw, Copy, RotateCw, Power,
  Users as UsersIcon, Webhook, Mail, ChevronDown, ChevronRight,
  Save, Pencil, X, Banknote, MapPin, Phone, AtSign, Tag,
  Upload, Image as ImageIcon, FileImage, Trash2, Sparkles, Check,
} from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const setPath = (obj, path, value) => {
  // Deep-clone shallow, set `path` (e.g. "address.street") to `value`. Pure.
  const out = { ...obj };
  const parts = path.split(".");
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
};

export default function SuperAdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api.get("/superadmin/tenants")
      .then((r) => setTenants(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Mandanten konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const rotateToken = async (id) => {
    if (!window.confirm("Wirklich neuen Webhook-Token erzeugen? Der alte verliert sofort Gültigkeit.")) return;
    try {
      const r = await api.post(`/superadmin/tenants/${id}/rotate-webhook-token`);
      toast.success("Neuer Token erzeugt");
      navigator.clipboard?.writeText(r.data.webhook_token);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Token-Rotation fehlgeschlagen");
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/superadmin/tenants/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Mandant deaktiviert" : "Mandant aktiviert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="superadmin-tenants-page">
        <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-xs text-[#F97316] tracking-[0.3em] uppercase mb-1">Administrator</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Building2 size={28} className="text-[#F97316]" />
              Mandanten-Verwaltung
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Unternehmen, die NOVATUS Sales Pro als White-Label nutzen.
              Jeder Mandant hat eigene Daten, eigene Vertriebsleiter/-mitarbeiter und einen eigenen Webhook-Token.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={load}
              data-testid="superadmin-tenants-refresh"
              className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold flex items-center gap-2">
              <RefreshCw size={16} /> Aktualisieren
            </button>
            <button onClick={() => setShowCreate(true)}
              data-testid="superadmin-tenant-new"
              className="h-10 px-4 rounded-lg bg-[#F97316] hover:bg-[#EA6A0A] text-[#09090B] text-sm font-bold flex items-center gap-2">
              <Plus size={16} /> Neuer Mandant
            </button>
          </div>
        </header>

        {loading ? (
          <div className="text-center py-12 text-zinc-400 text-sm font-medium">Lade …</div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">Noch keine Mandanten angelegt.</div>
        ) : (
          <div className="space-y-3" data-testid="superadmin-tenants-list">
            {tenants.map((t) => (
              <TenantRow key={t.id} tenant={t}
                open={openId === t.id}
                onToggle={() => setOpenId(openId === t.id ? null : t.id)}
                onRotateToken={() => rotateToken(t.id)}
                onToggleActive={() => toggleActive(t)}
                onReload={load}
              />
            ))}
          </div>
        )}

        {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={load} />}
      </main>
    </div>
  );
}

function TenantRow({ tenant, open, onToggle, onRotateToken, onToggleActive, onReload }) {
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !full) {
      api.get(`/superadmin/tenants/${tenant.id}`)
        .then((r) => { setFull(r.data); setDraft(r.data); })
        .catch(() => {});
    }
    if (!open) { setEditing(false); }
  }, [open, full, tenant.id]);

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("In Zwischenablage kopiert");
  };

  const update = (path, value) => setDraft((d) => setPath(d, path, value));

  const save = async () => {
    setSaving(true);
    try {
      // Send only the editable subset — backend TenantPatch ignores extras anyway.
      const payload = {
        name: draft.name,
        slug: draft.slug,
        logo_url: draft.logo_url || null,
        email_sender: draft.email_sender || null,
        default_rep_margin_pct: draft.default_rep_margin_pct == null ? null : Number(draft.default_rep_margin_pct),
        default_show_component_prices: !!draft.default_show_component_prices,
        brand_primary_color: (draft.brand_primary_color || "").trim() || null,
        brand_secondary_color: (draft.brand_secondary_color || "").trim() || null,
        address: draft.address || {},
        contact: draft.contact || {},
        billing: draft.billing || {},
        pricing_plan: draft.pricing_plan || {},
        energy_consultant: draft.energy_consultant || {},
        internal_bcc_email: (draft.internal_bcc_email || "").trim() || null,
      };
      const r = await api.patch(`/superadmin/tenants/${tenant.id}`, payload);
      setFull(r.data);
      setDraft(r.data);
      setEditing(false);
      toast.success("Mandant gespeichert");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(full); setEditing(false); };

  return (
    <div data-testid={`superadmin-tenant-${tenant.id}`}
      className={`bg-zinc-900 border-2 rounded-2xl overflow-hidden transition ${
        open ? "border-[#F97316]/40" : "border-zinc-800"
      } ${tenant.active ? "" : "opacity-60"}`}>
      <button onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={`font-mono text-[12px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${
              tenant.active ? "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40"
                            : "bg-zinc-800 text-zinc-500 border border-zinc-700"
            }`}>{tenant.active ? "aktiv" : "deaktiviert"}</span>
            <span className="font-mono text-[12px] text-zinc-300">slug: {tenant.slug}</span>
          </div>
          <h3 className="font-display text-lg font-bold leading-tight">{tenant.name}</h3>
          <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><UsersIcon size={11}/>
              {(tenant.headcount?.sales_director||0)}D · {(tenant.headcount?.sales_manager||0)}L · {(tenant.headcount?.sales_rep||0)}V
            </span>
            <span className="flex items-center gap-1 font-mono text-zinc-500">
              <Webhook size={11}/> {tenant.webhook_token_preview || "(kein Token)"}
            </span>
          </div>
        </div>
      </button>

      {open && full && draft && (
        <div className="border-t border-zinc-800 px-4 py-4 space-y-4 text-xs">
          {/* Webhook box stays on top — most-used info during onboarding. */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 mb-1 flex items-center gap-1">
              <Webhook size={11}/> Webhook-Token (Telesales-App-Konfiguration)
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-zinc-900 border border-zinc-800 rounded px-2 py-1 overflow-x-auto break-all">
                {full.webhook_token}
              </code>
              <button onClick={() => copy(full.webhook_token)}
                data-testid={`superadmin-tenant-copy-token-${tenant.id}`}
                className="px-2 h-8 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                <Copy size={14}/>
              </button>
              <button onClick={onRotateToken}
                data-testid={`superadmin-tenant-rotate-token-${tenant.id}`}
                className="px-2 h-8 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40">
                <RotateCw size={14}/>
              </button>
            </div>
            <p className="text-[12px] text-zinc-300 mt-1">
              Endpoint: <code>POST /api/webhook/leads</code> · Header: <code>Authorization: Bearer {full.webhook_token?.slice(0,18)}…</code>
            </p>
          </div>

          {/* Briefkopf-Branding: Logo + Letterhead-Upload */}
          <AssetUploadSection tenant={full} onChange={(updated) => { setFull(updated); setDraft(updated); }} />

          {/* Toolbar: Edit / Save / Cancel */}
          <div className="flex items-center justify-between">
            <div className="text-[12px] uppercase tracking-[0.2em] text-zinc-300 font-bold">
              Stamm- & Branding-Daten
            </div>
            {editing ? (
              <div className="flex gap-2">
                <button onClick={cancel}
                  data-testid={`superadmin-tenant-cancel-${tenant.id}`}
                  className="h-8 px-3 rounded-md text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1">
                  <X size={12}/> Abbrechen
                </button>
                <button onClick={save} disabled={saving}
                  data-testid={`superadmin-tenant-save-${tenant.id}`}
                  className="h-8 px-3 rounded-md text-xs font-bold bg-[#00E575] hover:bg-[#00C062] text-[#09090B] disabled:opacity-60 flex items-center gap-1">
                  <Save size={12}/> {saving ? "Speichern…" : "Speichern"}
                </button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)}
                data-testid={`superadmin-tenant-edit-${tenant.id}`}
                className="h-8 px-3 rounded-md text-xs font-semibold bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/30 flex items-center gap-1">
                <Pencil size={12}/> Bearbeiten
              </button>
            )}
          </div>

          {/* Group: Identifikation */}
          <SectionGrid title="Identifikation" icon={<Tag size={11}/>}>
            <FieldEdit label="Unternehmensname" value={draft.name} onChange={(v) => update("name", v)} editing={editing} testid={`tenant-name-${tenant.id}`} />
            <FieldEdit label="Slug (URL-Kennung)" value={draft.slug} onChange={(v) => update("slug", v)} editing={editing} mono />
            <FieldEdit label="Logo-URL" value={draft.logo_url || ""} onChange={(v) => update("logo_url", v)} editing={editing} placeholder="https://…" />
            <FieldEdit label="E-Mail-Absender" value={draft.email_sender || ""} onChange={(v) => update("email_sender", v)} editing={editing} icon={<Mail size={11}/>} placeholder='"Firma" <noreply@firma.de>' />
            <FieldEdit label="Interner BCC (Archiv)" value={draft.internal_bcc_email || ""} onChange={(v) => update("internal_bcc_email", v)} editing={editing} icon={<Mail size={11}/>} placeholder='archiv@firma.de; buchhaltung@firma.de' testid={`tenant-internal-bcc-${tenant.id}`} />
          </SectionGrid>

          {/* Group: Adresse */}
          <SectionGrid title="Geschäftsanschrift" icon={<MapPin size={11}/>}>
            <FieldEdit label="Straße + Hausnr." value={draft.address?.street || ""} onChange={(v) => update("address.street", v)} editing={editing} />
            <FieldEdit label="PLZ" value={draft.address?.zip || ""} onChange={(v) => update("address.zip", v)} editing={editing} />
            <FieldEdit label="Stadt" value={draft.address?.city || ""} onChange={(v) => update("address.city", v)} editing={editing} />
            <FieldEdit label="Land" value={draft.address?.country || ""} onChange={(v) => update("address.country", v)} editing={editing} placeholder="DE" />
          </SectionGrid>

          {/* Group: Kontakt */}
          <SectionGrid title="Kontakt" icon={<Phone size={11}/>}>
            <FieldEdit label="E-Mail" value={draft.contact?.email || ""} onChange={(v) => update("contact.email", v)} editing={editing} icon={<AtSign size={11}/>} />
            <FieldEdit label="Telefon" value={draft.contact?.phone || ""} onChange={(v) => update("contact.phone", v)} editing={editing} />
            <FieldEdit label="Fax" value={draft.contact?.fax || ""} onChange={(v) => update("contact.fax", v)} editing={editing} />
            <FieldEdit label="Website" value={draft.contact?.website || ""} onChange={(v) => update("contact.website", v)} editing={editing} placeholder="https://…" />
          </SectionGrid>

          {/* Group: Billing / Rechtlich */}
          <SectionGrid title="Rechtliches & Bankverbindung" icon={<Banknote size={11}/>}>
            <FieldEdit label="Firma (Rechnung)" value={draft.billing?.company_name || ""} onChange={(v) => update("billing.company_name", v)} editing={editing} />
            <FieldEdit label="Geschäftsführer" value={draft.billing?.managing_director || ""} onChange={(v) => update("billing.managing_director", v)} editing={editing} />
            <FieldEdit label="Amtsgericht" value={draft.billing?.court || ""} onChange={(v) => update("billing.court", v)} editing={editing} />
            <FieldEdit label="HRB" value={draft.billing?.hrb || ""} onChange={(v) => update("billing.hrb", v)} editing={editing} mono />
            <FieldEdit label="USt-ID" value={draft.billing?.ust_id || ""} onChange={(v) => update("billing.ust_id", v)} editing={editing} mono />
            <FieldEdit label="Steuernummer" value={draft.billing?.tax_number || ""} onChange={(v) => update("billing.tax_number", v)} editing={editing} mono />
            <FieldEdit label="Bank" value={draft.billing?.bank_name || ""} onChange={(v) => update("billing.bank_name", v)} editing={editing} />
            <FieldEdit label="IBAN" value={draft.billing?.iban || ""} onChange={(v) => update("billing.iban", v)} editing={editing} mono />
            <FieldEdit label="BIC" value={draft.billing?.bic || ""} onChange={(v) => update("billing.bic", v)} editing={editing} mono />
          </SectionGrid>

          {/* Group: Energieberater — empfängt automatisch das BzA-Datenpaket
              nach jeder unterschriebenen Auftragsbestätigung. */}
          <SectionGrid title="Energieberater (BzA-Empfänger)" icon={<AtSign size={11}/>}>
            <FieldEdit label="Name / Firma" value={draft.energy_consultant?.name || ""} onChange={(v) => update("energy_consultant.name", v)} editing={editing} />
            <FieldEdit label="E-Mail" value={draft.energy_consultant?.email || ""} onChange={(v) => update("energy_consultant.email", v)} editing={editing} icon={<AtSign size={11}/>} placeholder="berater@example.de" />
            <FieldEdit label="Telefon" value={draft.energy_consultant?.phone || ""} onChange={(v) => update("energy_consultant.phone", v)} editing={editing} />
            <FieldEdit label="EEE-Nummer (dena)" value={draft.energy_consultant?.eee_number || ""} onChange={(v) => update("energy_consultant.eee_number", v)} editing={editing} mono placeholder="z. B. 123456" />
          </SectionGrid>

          {/* Group: Marge + Kostenplan */}
          <SectionGrid title="Marge & monatlicher Kostenplan" icon={<Banknote size={11}/>}>
            <FieldEdit label="Standard-Vertriebsmarge %" value={draft.default_rep_margin_pct ?? ""} onChange={(v) => update("default_rep_margin_pct", v === "" ? null : v)} editing={editing} suffix="%" numeric />
            <FieldEdit label="Unternehmen / Monat" value={draft.pricing_plan?.company_fee_eur_per_month ?? 0} onChange={(v) => update("pricing_plan.company_fee_eur_per_month", Number(v) || 0)} editing={editing} suffix="€" numeric />
            <FieldEdit label="Direktor / Monat" value={draft.pricing_plan?.director_fee_eur_per_month ?? 0} onChange={(v) => update("pricing_plan.director_fee_eur_per_month", Number(v) || 0)} editing={editing} suffix="€" numeric />
            <FieldEdit label="Vertriebsleiter / Monat" value={draft.pricing_plan?.manager_fee_eur_per_month ?? 0} onChange={(v) => update("pricing_plan.manager_fee_eur_per_month", Number(v) || 0)} editing={editing} suffix="€" numeric />
            <FieldEdit label="Vertriebler / Monat" value={draft.pricing_plan?.rep_fee_eur_per_month ?? 0} onChange={(v) => update("pricing_plan.rep_fee_eur_per_month", Number(v) || 0)} editing={editing} suffix="€" numeric />
          </SectionGrid>

          {/* Group: Angebots-PDF-Defaults (Iteration 74) */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 mb-2 font-semibold">Angebots-PDF-Defaults</div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.default_show_component_prices}
                onChange={(e) => update("default_show_component_prices", e.target.checked)}
                disabled={!editing}
                data-testid="tenant-default-show-prices"
                className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-[#09090B] text-[#00E575] focus:ring-[#00E575] disabled:opacity-40"
              />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-zinc-100">Standardmäßig Einzelpreise anzeigen</div>
                <div className="text-[12px] font-medium text-zinc-400 leading-tight mt-0.5">
                  Setzt den Default-Wert des Toggles „Einzelpreise anzeigen" für alle neuen Angebote dieses Mandanten. Verkäufer:innen können pro Angebot noch überschreiben.
                </div>
              </div>
            </label>
          </div>

          {/* Group: Corporate-Farben (Iteration 88) */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 mb-2 font-semibold">Corporate-Farben (PDF & Charts)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ColorField
                label="Primärfarbe (Header, Charts)"
                value={draft.brand_primary_color || ""}
                onChange={(v) => update("brand_primary_color", v)}
                editing={editing}
                testId="tenant-brand-primary"
                placeholder="#0a328c"
              />
              <ColorField
                label="Sekundärfarbe (H3-Überschriften)"
                value={draft.brand_secondary_color || ""}
                onChange={(v) => update("brand_secondary_color", v)}
                editing={editing}
                testId="tenant-brand-secondary"
                placeholder="Fallback: Primär"
              />
            </div>
            <div className="text-[12px] font-medium text-zinc-400 leading-tight mt-2">
              Primär färbt das Header-Band und die PV-Ersparnis-Linie im Amortisations-Chart. Sekundär färbt die H3-Überschriften („Warum jetzt handeln?" u. ä.) im WP-Angebots-PDF. Leer lassen → Sekundär fällt auf Primär zurück, Primär auf das Novatus-Grün/Blau (Legacy).
            </div>
          </div>

          {/* Group: Headcount (read-only) */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 mb-2">Belegschaft (read-only)</div>
            <div className="grid grid-cols-3 gap-2">
              <ReadField label="Direktoren" value={full.headcount?.sales_director ?? 0} />
              <ReadField label="Vertriebsleiter" value={full.headcount?.sales_manager ?? 0} />
              <ReadField label="Vertriebler" value={full.headcount?.sales_rep ?? 0} />
            </div>
            {full.pricing_plan && !editing && (
              <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-2 gap-2">
                <ReadField label="Aktuell · Unternehmen" value={fmtEur(full.pricing_plan.company_fee_eur_per_month)} />
                <ReadField label="Aktuell · Direktor" value={fmtEur(full.pricing_plan.director_fee_eur_per_month)} />
                <ReadField label="Aktuell · Vertriebsleiter" value={fmtEur(full.pricing_plan.manager_fee_eur_per_month)} />
                <ReadField label="Aktuell · Vertriebler" value={fmtEur(full.pricing_plan.rep_fee_eur_per_month)} />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
            <button onClick={onToggleActive}
              data-testid={`superadmin-tenant-toggle-${tenant.id}`}
              className={`px-3 h-9 rounded-lg text-xs font-semibold border ${
                tenant.active
                  ? "bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border-rose-500/30"
                  : "bg-[#00E575]/15 hover:bg-[#00E575]/25 text-[#00E575] border-[#00E575]/30"
              }`}>
              <Power size={14} className="inline mr-1"/>
              {tenant.active ? "Deaktivieren" : "Aktivieren"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetUploadSection({ tenant, onChange }) {
  const [extractOpen, setExtractOpen] = useState(false);
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
      <div className="text-[12px] uppercase tracking-[0.18em] text-zinc-300 font-bold mb-3 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5"><FileImage size={11}/> Briefkopf-Branding</span>
        <button
          type="button"
          onClick={() => setExtractOpen(true)}
          data-testid="tenant-briefkopf-extract-button"
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 ring-1 ring-amber-500/40 text-amber-200 text-[12px] font-bold tracking-wider transition normal-case"
        >
          <Sparkles size={11} className="text-amber-300"/> AI · Briefkopf auslesen
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <AssetTile
          tenant={tenant} kind="logo"
          title="Firmen-Logo"
          hint="Erscheint oben rechts auf jeder PDF-Seite. PNG/JPG/SVG, max. 5 MB."
          onChange={onChange}
        />
        <AssetTile
          tenant={tenant} kind="letterhead"
          title="Briefkopf-Grafik"
          hint="Optional: Vollständiger Briefkopf-Header (z.B. mit Logo + Adress-Block in einem Bild). Überschreibt das Logo. PNG/JPG, max. 5 MB."
          onChange={onChange}
        />
      </div>
      {extractOpen && (
        <BriefkopfExtractModal
          tenant={tenant}
          onClose={() => setExtractOpen(false)}
          onApplied={(updated) => { onChange?.(updated); setExtractOpen(false); }}
        />
      )}
    </div>
  );
}

function BriefkopfExtractModal({ tenant, onClose, onApplied }) {
  const fileRef = useRef(null);
  const [stage, setStage] = useState("pick"); // pick | extracting | review | applying
  const [filename, setFilename] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);
  // Per-field "apply this value" checkbox state — defaults true for every
  // non-empty field, false for empty ones.
  const [chosen, setChosen] = useState({});

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setStage("extracting");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/superadmin/tenants/${tenant.id}/letterhead-extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const s = r.data?.suggestion || {};
      setSuggestion(s);
      // Seed chosen-map: pre-tick all non-empty fields.
      const next = {};
      const setIf = (path, v) => { next[path] = !!v; };
      setIf("name", s.name);
      for (const k of ["street","zip","city","country"]) setIf(`address.${k}`, s.address?.[k]);
      for (const k of ["email","phone","fax","website"]) setIf(`contact.${k}`, s.contact?.[k]);
      for (const k of ["company_name","managing_director","court","hrb","ust_id","tax_number","bank_name","iban","bic"]) setIf(`billing.${k}`, s.billing?.[k]);
      setChosen(next);
      setStage("review");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Extraktion fehlgeschlagen");
      setStage("pick");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    // Build the patch from the chosen subset, never overwriting existing
    // non-empty tenant fields (visual cue is already there + we set on server too).
    if (!suggestion) return;
    setStage("applying");
    const patch = { address: {...(tenant.address||{})}, contact: {...(tenant.contact||{})}, billing: {...(tenant.billing||{})} };
    if (chosen["name"] && suggestion.name && !tenant.name) patch.name = suggestion.name;
    for (const k of ["street","zip","city","country"]) {
      if (chosen[`address.${k}`] && suggestion.address?.[k] && !tenant.address?.[k]) {
        patch.address[k] = suggestion.address[k];
      }
    }
    for (const k of ["email","phone","fax","website"]) {
      if (chosen[`contact.${k}`] && suggestion.contact?.[k] && !tenant.contact?.[k]) {
        patch.contact[k] = suggestion.contact[k];
      }
    }
    for (const k of ["company_name","managing_director","court","hrb","ust_id","tax_number","bank_name","iban","bic"]) {
      if (chosen[`billing.${k}`] && suggestion.billing?.[k] && !tenant.billing?.[k]) {
        patch.billing[k] = suggestion.billing[k];
      }
    }
    try {
      const r = await api.patch(`/superadmin/tenants/${tenant.id}`, patch);
      toast.success("Briefkopf-Daten übernommen");
      onApplied?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
      setStage("review");
    }
  };

  const toggle = (path) => setChosen((c) => ({ ...c, [path]: !c[path] }));

  return (
    <div className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center px-3 py-8 overflow-y-auto">
      <div className="w-full max-w-2xl bg-zinc-900 border-2 border-amber-500/40 rounded-2xl p-5 space-y-3"
        data-testid="briefkopf-extract-modal"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Sparkles size={20} className="text-amber-300"/> Briefkopf · AI-Auslese
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={18}/></button>
        </div>

        {stage === "pick" && (
          <div className="space-y-3">
            <p className="text-xs text-zinc-300 font-medium">
              Lade einen vorhandenen Firmen-Briefkopf hoch (PDF, PNG, JPG, WEBP — max. 6 MB).
              Gemini 3 Pro liest Firmenname, Adresse, Kontakt, HRB, USt-ID, Steuernummer, IBAN/BIC
              automatisch aus. Anschließend kannst du jedes Feld einzeln übernehmen oder verwerfen.
            </p>
            <input
              type="file" ref={fileRef}
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={onPick} className="hidden"
              data-testid="briefkopf-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="briefkopf-pick-file-button"
              className="w-full h-24 rounded-lg border-2 border-dashed border-amber-500/40 hover:border-amber-500/70 bg-amber-500/5 hover:bg-amber-500/10 text-amber-200 font-semibold transition flex flex-col items-center justify-center gap-1.5"
            >
              <Upload size={20}/>
              <span>Briefkopf-Datei auswählen</span>
              <span className="text-[12px] text-zinc-300 font-normal">PDF · PNG · JPG · WEBP</span>
            </button>
            {error && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2">{error}</div>}
          </div>
        )}

        {stage === "extracting" && (
          <div className="py-12 text-center text-amber-200 space-y-2">
            <Sparkles size={32} className="mx-auto animate-pulse text-amber-300"/>
            <div className="font-semibold">Gemini liest «{filename}» …</div>
            <div className="text-xs text-zinc-400 font-medium">Das dauert üblicherweise 5–15 Sekunden.</div>
          </div>
        )}

        {stage === "review" && suggestion && (
          <div className="space-y-3">
            <p className="text-xs text-zinc-300 font-medium">
              Hake jedes Feld an, das übernommen werden soll. <b>Vorhandene Werte werden nie überschrieben</b> —
              graue Zeilen sind bereits gesetzt und bleiben unangetastet.
            </p>
            <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              <SuggestionRow label="Firmenname" path="name"
                suggested={suggestion.name} existing={tenant.name}
                checked={!!chosen.name} onToggle={() => toggle("name")} />
              <SuggestionGroup title="Adresse">
                {["street","zip","city","country"].map((k) => (
                  <SuggestionRow key={k} label={({street:"Straße",zip:"PLZ",city:"Stadt",country:"Land"})[k]}
                    path={`address.${k}`}
                    suggested={suggestion.address?.[k]} existing={tenant.address?.[k]}
                    checked={!!chosen[`address.${k}`]} onToggle={() => toggle(`address.${k}`)} />
                ))}
              </SuggestionGroup>
              <SuggestionGroup title="Kontakt">
                {["email","phone","fax","website"].map((k) => (
                  <SuggestionRow key={k} label={({email:"E-Mail",phone:"Telefon",fax:"Fax",website:"Website"})[k]}
                    path={`contact.${k}`}
                    suggested={suggestion.contact?.[k]} existing={tenant.contact?.[k]}
                    checked={!!chosen[`contact.${k}`]} onToggle={() => toggle(`contact.${k}`)} />
                ))}
              </SuggestionGroup>
              <SuggestionGroup title="Rechtliches & Bank">
                {[
                  ["company_name","Firma (Rechnung)"],
                  ["managing_director","Geschäftsführer"],
                  ["court","Amtsgericht"],
                  ["hrb","HRB"],
                  ["ust_id","USt-ID"],
                  ["tax_number","Steuernummer"],
                  ["bank_name","Bank"],
                  ["iban","IBAN"],
                  ["bic","BIC"],
                ].map(([k, label]) => (
                  <SuggestionRow key={k} label={label} path={`billing.${k}`}
                    suggested={suggestion.billing?.[k]} existing={tenant.billing?.[k]}
                    checked={!!chosen[`billing.${k}`]} onToggle={() => toggle(`billing.${k}`)}
                    mono={["hrb","ust_id","tax_number","iban","bic"].includes(k)} />
                ))}
              </SuggestionGroup>
            </div>
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-800">
              <button onClick={() => setStage("pick")}
                data-testid="briefkopf-retry-button"
                className="h-9 px-3 rounded-md text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                Andere Datei
              </button>
              <button onClick={apply}
                data-testid="briefkopf-apply-button"
                className="h-9 px-4 rounded-md text-xs font-bold bg-[#00E575] hover:bg-[#00C062] text-[#09090B] flex items-center gap-1.5">
                <Check size={13}/> Auswahl übernehmen
              </button>
            </div>
          </div>
        )}

        {stage === "applying" && (
          <div className="py-8 text-center text-amber-200">Speichere …</div>
        )}
      </div>
    </div>
  );
}

function SuggestionGroup({ title, children }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-[0.18em] text-zinc-300 font-bold mb-1 mt-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function SuggestionRow({ label, path, suggested, existing, checked, onToggle, mono }) {
  const hasSuggestion = suggested != null && suggested !== "";
  const hasExisting = existing != null && existing !== "";
  const locked = hasExisting; // existing values never overwritten
  return (
    <div
      data-testid={`briefkopf-row-${path}`}
      className={`flex items-start gap-2 px-2 py-1.5 rounded-md ${locked ? "bg-zinc-950" : hasSuggestion ? "bg-amber-500/5 hover:bg-amber-500/10" : "bg-zinc-950 opacity-50"}`}
    >
      <input
        type="checkbox"
        disabled={!hasSuggestion || locked}
        checked={checked && hasSuggestion && !locked}
        onChange={onToggle}
        data-testid={`briefkopf-row-check-${path}`}
        className="mt-1 accent-amber-400 w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
      />
      <div className="flex-1 min-w-0 grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <div className="text-[12px] uppercase tracking-wider text-zinc-300">{label}</div>
          <div className={`text-xs ${mono ? "font-mono" : ""} ${locked ? "text-zinc-300" : "text-amber-100"}`}>
            {hasSuggestion ? String(suggested) : <span className="text-zinc-600">— nicht erkannt</span>}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[12px] uppercase tracking-wider text-zinc-400">Bestehender Wert</div>
          <div className={`text-xs ${mono ? "font-mono" : ""} ${hasExisting ? "text-zinc-300" : "text-zinc-400"}`}>
            {hasExisting ? String(existing) : "— leer —"}
          </div>
        </div>
      </div>
      {locked && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 self-center">geschützt</span>}
    </div>
  );
}

function AssetTile({ tenant, kind, title, hint, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  const storagePath = tenant?.[`${kind}_storage_path`];
  const url = storagePath ? `/api${tenant?.[`${kind}_url`]?.replace(/^\/api/, "") || `/tenants/${tenant.id}/${kind}`}` : null;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/superadmin/tenants/${tenant.id}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${title} hochgeladen`);
      setBust(Date.now());
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm(`${title} wirklich löschen?`)) return;
    try {
      const r = await api.delete(`/superadmin/tenants/${tenant.id}/${kind}`);
      toast.success("Entfernt");
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-bold text-zinc-200 flex items-center gap-1.5">
            <ImageIcon size={12} className="text-amber-300"/> {title}
          </div>
          <div className="text-[12px] text-zinc-300 mt-0.5">{hint}</div>
        </div>
      </div>
      <div className="bg-[#FFFFFF0A] border border-zinc-800 rounded-md h-28 flex items-center justify-center overflow-hidden">
        {storagePath ? (
          // Auth-gated image — uses axios via `<img>` is not authenticated;
          // we fetch via blob to honor the bearer token in localStorage.
          <AuthedImage url={url} bust={bust} alt={title} />
        ) : (
          <span className="text-[12px] text-zinc-400">Noch kein {title}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={fileRef} type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={onPick} className="hidden"
          data-testid={`tenant-${kind}-file-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`tenant-${kind}-upload-button`}
          className="flex-1 h-9 px-3 rounded-md text-xs font-semibold bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/30 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Upload size={12}/> {uploading ? "Lade hoch…" : storagePath ? "Ersetzen" : "Hochladen"}
        </button>
        {storagePath && (
          <button
            type="button"
            onClick={remove}
            data-testid={`tenant-${kind}-remove-button`}
            className="h-9 px-2 rounded-md text-xs font-semibold bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30"
          >
            <Trash2 size={12}/>
          </button>
        )}
      </div>
    </div>
  );
}

function AuthedImage({ url, bust, alt }) {
  // Reads the bearer token from localStorage and fetches the asset as a blob
  // so the rendered <img> doesn't need cookies / a public URL.
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    const token = localStorage.getItem("novatus_token");
    if (!url || !token) { setSrc(null); return undefined; }
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject(r.status))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, bust]);
  if (!src) return <span className="text-[12px] text-zinc-400">lädt…</span>;
  return <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />;
}

function SectionGrid({ title, icon, children }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-[0.18em] text-zinc-300 font-bold mb-2 flex items-center gap-1.5">
        {icon} {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function FieldEdit({ label, value, onChange, editing, mono, icon, placeholder, suffix, numeric, testid }) {
  if (!editing) {
    const display = value === "" || value == null
      ? "—"
      : suffix ? `${value} ${suffix}` : String(value);
    return (
      <div className="flex items-start gap-2 min-w-0 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
        <div className="text-[12px] uppercase tracking-wider text-zinc-300 w-32 shrink-0 flex items-center gap-1 mt-0.5">
          {icon} {label}
        </div>
        <div className={`text-zinc-200 break-all ${mono ? "font-mono" : ""}`} data-testid={testid}>{display}</div>
      </div>
    );
  }
  return (
    <label className="flex flex-col gap-1 text-zinc-300">
      <span className="text-[12px] uppercase tracking-wider text-zinc-300 flex items-center gap-1">{icon} {label}</span>
      <div className="flex items-center gap-1">
        <input
          data-testid={testid}
          type={numeric ? "number" : "text"}
          step={numeric ? "0.01" : undefined}
          value={value ?? ""}
          placeholder={placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-zinc-100 focus:outline-none focus:border-[#F97316] ${mono ? "font-mono text-[12px]" : "text-xs"}`}
        />
        {suffix && <span className="text-[12px] text-zinc-300">{suffix}</span>}
      </div>
    </label>
  );
}

function ReadField({ label, value }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <div className="text-[12px] uppercase tracking-wider text-zinc-300 w-32 shrink-0">{label}</div>
      <div className="text-zinc-200 truncate">{value}</div>
    </div>
  );
}

function ColorField({ label, value, onChange, editing, placeholder, testId }) {
  const hex = (value || "").trim();
  const isValid = /^#[0-9a-fA-F]{6}$/.test(hex);
  if (!editing) {
    return (
      <div className="flex items-start gap-2 min-w-0 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
        <div className="text-[12px] uppercase tracking-wider text-zinc-300 flex-1">{label}</div>
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block w-5 h-5 rounded border border-zinc-700"
            style={{ background: isValid ? hex : "transparent" }}
          />
          <span className="text-zinc-200 font-mono text-[12px]" data-testid={testId}>
            {hex || "—"}
          </span>
        </div>
      </div>
    );
  }
  return (
    <label className="flex flex-col gap-1 text-zinc-300">
      <span className="text-[12px] uppercase tracking-wider text-zinc-300">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={isValid ? hex : "#0a328c"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 rounded border border-zinc-800 bg-zinc-950 cursor-pointer"
          data-testid={testId ? `${testId}-picker` : undefined}
        />
        <input
          type="text"
          value={hex}
          placeholder={placeholder || "#RRGGBB"}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-zinc-100 focus:outline-none focus:border-[#F97316] font-mono text-[12px]"
          data-testid={testId}
        />
      </div>
      {hex && !isValid && (
        <div className="text-[11px] text-rose-400">Ungültig — bitte Format #RRGGBB.</div>
      )}
    </label>
  );
}

function CreateTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", slug: "",
    director_first_name: "", director_last_name: "",
    director_email: "", director_phone: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
      await api.post("/superadmin/tenants", payload);
      toast.success("Mandant angelegt");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
      <form onSubmit={submit}
        data-testid="superadmin-tenant-create-modal"
        className="w-full max-w-md bg-zinc-900 border-2 border-[#F97316]/40 rounded-2xl p-5 space-y-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Building2 size={22} className="text-[#F97316]"/> Neuer Mandant
        </h2>
        <Input label="Unternehmensname" value={form.name} onChange={(v) => setForm({...form, name: v})}
          required testid="new-tenant-name"/>
        <Input label="Slug (optional, sonst automatisch)" value={form.slug} onChange={(v) => setForm({...form, slug: v})}
          placeholder="z.B. beispiel-gmbh" testid="new-tenant-slug"/>
        <div className="pt-2 border-t border-zinc-800 text-[12px] uppercase tracking-wider text-zinc-300">
          Vertriebsdirektor (optional — kann auch später hinzugefügt werden)
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Vorname" value={form.director_first_name} onChange={(v) => setForm({...form, director_first_name: v})}/>
          <Input label="Nachname" value={form.director_last_name} onChange={(v) => setForm({...form, director_last_name: v})}/>
        </div>
        <Input label="E-Mail" value={form.director_email} onChange={(v) => setForm({...form, director_email: v})}
          type="email" testid="new-tenant-director-email"/>
        <Input label="Telefon" value={form.director_phone} onChange={(v) => setForm({...form, director_phone: v})}
          placeholder="+49 …"/>

        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose}
            data-testid="superadmin-tenant-create-cancel"
            className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold">
            Abbrechen
          </button>
          <button type="submit" disabled={saving}
            data-testid="superadmin-tenant-create-submit"
            className="h-10 px-4 rounded-lg bg-[#F97316] hover:bg-[#EA6A0A] text-[#09090B] text-sm font-bold disabled:opacity-50">
            {saving ? "Anlegen…" : "Anlegen"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] uppercase tracking-wider text-zinc-300 mb-1 block">{label}</span>
      <input type={type} value={value} required={required} placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-[#F97316]"/>
    </label>
  );
}
